import type { AgentSecret } from "@ext/agent/mcp/agentSecretStore";
import t from "@ext/localization/locale/translate";
import { z } from "zod";

/** Trims surrounding whitespace only — a secret name has no other restrictions, spaces and punctuation included. */
export const normalizeSecretKey = (key: string): string => key.trim();

const secretKindSchema = z.enum(["token", "login"]);

/** `login`/`kind` are UI-only — the backend only ever stores a flat `key -> value` map. Only `key` is
 *  required: a row's login/password/token may be saved empty and filled in later. */
const secretRowSchema = z.object({
	id: z.string(),
	key: z.string().min(1),
	savedKey: z.string(),
	savedSecret: z.custom<AgentSecret>().optional(),
	value: z.string(),
	kind: secretKindSchema,
	login: z.string(),
});

export const agentSecretsFormSchema = z.object({
	rows: z.array(secretRowSchema).superRefine((rows, ctx) => {
		const indicesByKey = new Map<string, number[]>();
		rows.forEach((row, index) => {
			const key = normalizeSecretKey(row.key);
			if (!key) return;
			indicesByKey.set(key, [...(indicesByKey.get(key) ?? []), index]);
		});
		for (const indices of indicesByKey.values()) {
			if (indices.length < 2) continue;
			for (const index of indices) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("app-settings.keys-passwords.key-duplicate"),
					path: [index, "key"],
				});
			}
		}
	}),
});

export type SecretKind = z.infer<typeof secretKindSchema>;
export type AgentSecretsFormData = z.infer<typeof agentSecretsFormSchema>;
export type SecretRow = AgentSecretsFormData["rows"][number];
